/**
 * Discovery / Inventory - Phase 2 wiring + Phase-2-hardening pass.
 *
 * Live data sources:
 *   • /v1/discovery/assets       → list + aggregate
 *   • /v1/discovery/assets/:id   → single-asset detail (drawer)
 *
 * Aggregates assets per (sourceType) for the bar chart + sources
 * table. The hardening pass added the per-asset detail drawer that
 * the original cut deferred: clicking any individual asset row
 * fetches `getDiscoveryAsset(id)` and renders classifications,
 * metadata, source-of-truth raw fields, and a per-asset findings
 * count.
 */

import { useRegulationText } from '@/lib/regulation-terms';
import { useEffect, useMemo, useState } from 'react';
import { motion } from 'motion/react';
import { Database, Server, HardDrive, Cloud, Loader2, X, AlertTriangle, Search, User } from 'lucide-react';
import { GlassCard, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { ErrorState, LoadingState } from '@foctta/ui-shell';
import { KpiCard } from '@/components/viz/KpiCard';
import { CrossSourceReconciliationCard } from './CrossSourceReconciliation';
import { VirtualList } from '@/components/ui/virtual-list';
import { PageGuide } from '@/components/ui/page-guide';
import { cn } from '@/lib/cn';
import { ReidentificationRiskPanel } from './ReidentificationRiskPanel';
import {
  BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip as RTooltip, CartesianGrid,
} from 'recharts';
import {
  listDiscoveryAssets,
  getDiscoveryAsset,
  assignAssetOwner,
  getAssetInventorySummary,
  type AssetInventorySummary,
  type DiscoveryAsset,
} from '@/lib/api';
import { ProtectedCopyPanel } from './ProtectedCopyPanel';
import { StoreDensityCard } from './StoreDensityCard';

const ICON_FOR_SOURCE_TYPE: Record<string, React.ComponentType<{ className?: string }>> = {
  postgres: Database,
  mysql: Database,
  oracle: Database,
  mssql: Database,
  mariadb: Database,
  cockroachdb: Database,
  snowflake: Server,
  bigquery: Server,
  redshift: Server,
  databricks: Server,
  azure_synapse: Server,
  mongodb: Database,
  cassandra: Database,
  elasticsearch: Database,
  redis: Server,
  s3: HardDrive,
  azure_blob: HardDrive,
  gcs: HardDrive,
  cloudflare_r2: HardDrive,
  m365_sharepoint: Cloud,
  m365_onedrive: Cloud,
  m365_exchange: Cloud,
  m365_teams: Cloud,
  google_workspace_drive: Cloud,
};

interface SourceAggregate {
  sourceType: string;
  assetCount: number;
  classifiedAssetCount: number;
  totalClassifications: number;
  sensitiveAssetCount: number;
}

const SENSITIVITY_RANK: Record<string, number> = { critical: 4, high: 3, medium: 2, low: 1, unknown: 0 };

/**
 * An asset created by a person's schema-only DDL upload, not by a scan.
 *
 * The controller keys an upload's asset in its own namespace
 * (`upload://<unit or tenant-wide>/<declared ref>`) and records
 * `provenance: 'operator_upload'` and `declared_ref` in its external refs, so
 * it never merges with the scanned asset of the same table (review of PR #83).
 * The two are listed side by side under the same name; the upload is labelled
 * wherever it appears. Read from the external refs the controller writes, never
 * from free-form metadata.
 */
export function isUploadedSchemaAsset(a: Pick<DiscoveryAsset, 'externalRefs'>): boolean {
  const refs = a.externalRefs ?? {};
  return refs['provenance'] === 'operator_upload' || (refs['ref'] ?? '').toLowerCase().startsWith('upload://');
}

/** The detail drawer's note for an uploaded schema's asset; nothing for any other asset. */
export function UploadedSchemaNote({ asset }: { asset: DiscoveryAsset }) {
  if (!isUploadedSchemaAsset(asset)) return null;
  const declared = asset.externalRefs?.['declared_ref'] ?? null;
  return (
    <div className="rounded-lg border border-info/40 bg-info/5 p-3" data-testid="uploaded-schema-note">
      <div className="text-sm font-semibold text-info">Uploaded schema - not scanned</div>
      <div className="text-[11px] text-muted-foreground mt-1">
        A person uploaded this table&apos;s definition; its classifications are inferred from column names and no scanner
        read the data.
        {declared && (
          <>
            {' '}Declared reference: <code className="font-mono text-[10px]">{declared}</code>.
          </>
        )}
      </div>
      <div className="text-[11px] text-muted-foreground mt-1">
        It is kept apart from any scanned asset with the same reference: the upload never changes what a scan found,
        and a scan never lands here. Search the inventory for <code className="font-mono text-[10px]">{asset.displayName}</code>{' '}
        to see a scanned asset beside it.
      </div>
    </div>
  );
}

export interface AssetCatalogFilter {
  search: string;
  source: string;
  sensitiveOnly: boolean;
  unownedOnly: boolean;
  sort: 'risk' | 'name' | 'recent';
}

/** Pure filter+sort for the asset catalog grid (doc 33). Exported for tests. */
export function filterSortAssets(assets: DiscoveryAsset[], f: AssetCatalogFilter): DiscoveryAsset[] {
  const q = f.search.trim().toLowerCase();
  const filtered = assets.filter((a) => {
    if (f.source !== 'all' && a.sourceType !== f.source) return false;
    if (f.sensitiveOnly && !(a.sensitivity === 'critical' || a.sensitivity === 'high')) return false;
    if (f.unownedOnly && a.ownerEmail) return false;
    if (q) {
      const hay = `${a.displayName} ${a.sourceType} ${a.ownerEmail ?? ''} ${(a.classifications ?? []).map((c) => c.label).join(' ')}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
  return [...filtered].sort((a, b) => {
    if (f.sort === 'name') return (a.displayName || a.id).localeCompare(b.displayName || b.id);
    if (f.sort === 'recent') return (b.lastScannedAt ?? '').localeCompare(a.lastScannedAt ?? '');
    const sr = (SENSITIVITY_RANK[b.sensitivity] ?? 0) - (SENSITIVITY_RANK[a.sensitivity] ?? 0);
    if (sr !== 0) return sr;
    return (b.classifications?.length ?? 0) - (a.classifications?.length ?? 0);
  });
}
const SENS_PILL_CLS: Record<string, string> = {
  critical: 'bg-sev-critical/15 text-sev-critical',
  high: 'bg-sev-high/15 text-sev-high',
  medium: 'bg-sev-medium/15 text-sev-medium',
  low: 'bg-sev-low/15 text-sev-low',
  unknown: 'bg-muted/40 text-muted-foreground',
};

export function DiscoveryInventory() {
  const tx = useRegulationText();
  const [assets, setAssets] = useState<DiscoveryAsset[]>([]);
  /** Totals over every asset (server). `assets` is only the page shown in the catalog. */
  const [summary, setSummary] = useState<AssetInventorySummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /** doc 33 - asset catalog grid: search / filter / sort over individual assets. */
  const [assetSearch, setAssetSearch] = useState('');
  const [assetSource, setAssetSource] = useState<string>('all');
  const [assetSensitiveOnly, setAssetSensitiveOnly] = useState(false);
  const [assetUnownedOnly, setAssetUnownedOnly] = useState(false);
  const [assetSort, setAssetSort] = useState<'risk' | 'name' | 'recent'>('risk');

  /** Detail drawer state: open asset, full payload from GET /assets/:id, load + error states. */
  const [drawerAssetId, setDrawerAssetId] = useState<string | null>(null);
  const [drawerAsset, setDrawerAsset] = useState<DiscoveryAsset | null>(null);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [drawerError, setDrawerError] = useState<string | null>(null);

  /** Item-1: assign a data custodian (owner) to the open asset. */
  const [ownerInput, setOwnerInput] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [assignError, setAssignError] = useState<string | null>(null);

  async function handleAssignOwner() {
    if (!drawerAssetId) return;
    setAssigning(true);
    setAssignError(null);
    try {
      const updated = await assignAssetOwner(drawerAssetId, ownerInput.trim());
      setDrawerAsset(updated);
      setOwnerInput(updated.ownerEmail ?? '');
      void refresh(); // reflect the owner / orphan-state change in the list
    } catch (err) {
      setAssignError(err instanceof Error ? err.message : 'Failed to assign owner');
    } finally {
      setAssigning(false);
    }
  }

  const refresh = (signal?: AbortSignal) =>
    Promise.all([listDiscoveryAssets(), getAssetInventorySummary()])
      .then(([res, sum]) => {
        if (signal?.aborted) return;
        setAssets(res.assets);
        setSummary(sum);
        setError(null);
      })
      .catch((err) => {
        if (signal?.aborted) return;
        setError(err instanceof Error ? err.message : 'Failed to load assets');
      })
      .finally(() => {
        if (!signal?.aborted) setLoading(false);
      });

  useEffect(() => {
    const ctrl = new AbortController();
    refresh(ctrl.signal);
    return () => ctrl.abort();
  }, []);

  // Findings -> Inventory handoff: a finding's "Assign a data owner" action
  // stashes the asset ref so we land pre-filtered on that asset.
  useEffect(() => {
    try {
      const q = sessionStorage.getItem('discovery.inventorySearch');
      if (q) {
        sessionStorage.removeItem('discovery.inventorySearch');
        setAssetSearch(q);
      }
    } catch {
      /* sessionStorage may be unavailable */
    }
  }, []);

  // When an asset is selected, fetch the full detail payload from
  // GET /v1/discovery/assets/:id. The list endpoint trims fields for
  // payload size; the detail endpoint carries full classifications,
  // metadata, sample-row hashes (per RLS), and provenance.
  useEffect(() => {
    if (!drawerAssetId) return;
    const ctrl = new AbortController();
    setDrawerLoading(true);
    setDrawerError(null);
    setAssignError(null);
    getDiscoveryAsset(drawerAssetId)
      .then((res) => {
        if (ctrl.signal.aborted) return;
        setDrawerAsset(res);
        setOwnerInput(res.ownerEmail ?? '');
      })
      .catch((err) => !ctrl.signal.aborted && setDrawerError(err instanceof Error ? err.message : 'Failed to load asset'))
      .finally(() => !ctrl.signal.aborted && setDrawerLoading(false));
    return () => ctrl.abort();
  }, [drawerAssetId]);

  function closeDrawer() {
    setDrawerAssetId(null);
    setDrawerAsset(null);
    setDrawerError(null);
  }

  // Per-source table and chart, and the KPIs: counted by the server over every
  // asset. They used to be tallied from `assets` - the first page of the list
  // (100 by default) - so past one page every figure here was short
  // (release 2.1 item 4).
  const sourceAggregates: SourceAggregate[] = useMemo(
    () =>
      (summary?.bySourceType ?? []).map((s) => ({
        sourceType: s.sourceType,
        assetCount: s.assetCount,
        classifiedAssetCount: s.classifiedAssetCount,
        totalClassifications: s.totalClassifications,
        sensitiveAssetCount: s.sensitiveAssetCount,
      })),
    [summary],
  );

  const totalAssets = summary?.totalAssets ?? 0;
  const totalSources = sourceAggregates.length;
  const totalClassified = summary?.classifiedAssets ?? 0;
  const totalSensitive = summary?.sensitiveAssets ?? 0;

  // doc 33 - the searchable / filterable / sortable / virtualized catalog list.
  const catalogAssets = useMemo(
    () => filterSortAssets(assets, {
      search: assetSearch,
      source: assetSource,
      sensitiveOnly: assetSensitiveOnly,
      unownedOnly: assetUnownedOnly,
      sort: assetSort,
    }),
    [assets, assetSearch, assetSource, assetSensitiveOnly, assetUnownedOnly, assetSort],
  );

  return (
    <PageContainer>
      <PageHeader
        title="Data Catalog"
        subtitle={tx("Every classified PII column across your connected databases and cloud sources, aggregated by source type. Files on laptops are in Endpoint Data.")}
        badges={[
          { label: `${totalAssets} assets`, variant: 'glass' },
          { label: `${totalSources} source types`, variant: 'glass' },
          ...(loading
            ? [{ label: 'Loading', variant: 'glass' as const }]
            : error
              ? [{ label: 'Load error', variant: 'warning' as const }]
              : [{ label: 'Live', variant: 'success' as const }]),
        ]}
      />

      <PageGuide id="inventory">
        Every place sensitive data was found, with who owns it. Search your data, filter to the
        sensitive or unowned items, and assign a data owner to anything that has none - unowned
        sensitive data is a common audit gap.
      </PageGuide>

      {loading && (
        <LoadingState caption="Loading inventory…" />
      )}

      {error && !loading && (
        <ErrorState
          title="Failed to load assets"
          detail={error}
          onRetry={() => { setError(null); setLoading(true); refresh(); }}
        />
      )}

      {!loading && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <KpiCard label="Source types" value={totalSources} icon={Cloud} />
            <KpiCard label="Total assets" value={totalAssets} icon={Database} />
            <KpiCard
              label="Classified assets"
              value={totalClassified}
              icon={HardDrive}
              hint={`${totalAssets > 0 ? ((totalClassified / totalAssets) * 100).toFixed(0) : 0}% coverage`}
            />
            <KpiCard
              label={tx("Sensitive PII assets")}
              value={totalSensitive}
              icon={Server}
              glow={totalSensitive > 0 ? 'medium' : 'low'}
              hint="Aadhaar / PAN / minor / health"
            />
          </div>

          {sourceAggregates.length > 0 ? (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
                <GlassCard className="lg:col-span-2 p-6">
                  <CardHeader className="p-0 mb-4">
                    <CardTitle>Classified assets by source type</CardTitle>
                  </CardHeader>
                  {/* doc 33 - the bar chart only reads well with a few source
                      types to compare. With 1-2 it drew two lonely full-height
                      bars (the audit's "glitch"); show a clean stat strip instead. */}
                  {sourceAggregates.length >= 3 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={sourceAggregates} margin={{ top: 10, right: 10, left: 0, bottom: 30 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                        <XAxis
                          dataKey="sourceType"
                          tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }}
                          stroke="hsl(var(--border))"
                          angle={-15}
                          dy={8}
                        />
                        <YAxis tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }} stroke="hsl(var(--border))" allowDecimals={false} />
                        <RTooltip
                          contentStyle={{
                            background: 'hsl(var(--popover))',
                            border: '1px solid hsl(var(--border))',
                            borderRadius: 8,
                          }}
                          cursor={{ fill: 'hsl(var(--muted) / 0.3)' }}
                        />
                        <Bar dataKey="classifiedAssetCount" name="Classified" fill="hsl(var(--chart-1))" radius={[6, 6, 0, 0]} />
                        <Bar dataKey="sensitiveAssetCount" name="Sensitive" fill="hsl(var(--chart-7))" radius={[6, 6, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="space-y-3">
                      {sourceAggregates.map((a) => {
                        const Icon = ICON_FOR_SOURCE_TYPE[a.sourceType] ?? Database;
                        const pct = a.assetCount > 0 ? Math.round((a.classifiedAssetCount / a.assetCount) * 100) : 0;
                        return (
                          <div key={a.sourceType} className="glass-lite rounded-lg p-4">
                            <div className="flex items-center gap-2 mb-3">
                              <Icon className="h-4 w-4 text-primary" />
                              <span className="text-sm font-medium">{a.sourceType}</span>
                              <Badge variant="glass" className="text-[9px] ml-auto">{pct}% classified</Badge>
                            </div>
                            <div className="grid grid-cols-3 gap-3 text-center">
                              <div>
                                <div className="text-2xl font-semibold tabular">{a.assetCount.toLocaleString('en-IN')}</div>
                                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Assets</div>
                              </div>
                              <div>
                                <div className="text-2xl font-semibold tabular text-primary">{a.classifiedAssetCount.toLocaleString('en-IN')}</div>
                                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Classified</div>
                              </div>
                              <div>
                                <div className={cn('text-2xl font-semibold tabular', a.sensitiveAssetCount > 0 ? 'text-sev-high' : 'text-muted-foreground')}>
                                  {a.sensitiveAssetCount.toLocaleString('en-IN')}
                                </div>
                                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Sensitive</div>
                              </div>
                            </div>
                            {/* Coverage bar */}
                            <div className="mt-3 h-1.5 rounded-full bg-muted/40 overflow-hidden">
                              <div className="h-full bg-primary rounded-full" style={{ width: `${pct}%` }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </GlassCard>

                <GlassCard className="p-6">
                  <CardHeader className="p-0 mb-3">
                    <CardTitle>Top sources</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0 space-y-2">
                    {sourceAggregates.slice(0, 7).map((a, i) => {
                      const Icon = ICON_FOR_SOURCE_TYPE[a.sourceType] ?? Database;
                      return (
                        <motion.div
                          key={a.sourceType}
                          initial={{ opacity: 0, x: -8 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: i * 0.04 }}
                          className="flex items-center gap-3 p-2 rounded-md hover:bg-muted/40 transition-colors"
                        >
                          <Icon className="h-4 w-4 text-primary" />
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-medium truncate">{a.sourceType}</div>
                            <div className="text-[10px] text-muted-foreground">
                              {a.assetCount.toLocaleString('en-IN')} assets · {a.classifiedAssetCount.toLocaleString('en-IN')} classified
                            </div>
                          </div>
                          <span className="h-1.5 w-1.5 rounded-full bg-success" />
                        </motion.div>
                      );
                    })}
                  </CardContent>
                </GlassCard>
              </div>

              <GlassCard className="p-0 overflow-hidden">
                <div className="grid grid-cols-12 px-4 py-2 text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border/40">
                  <div className="col-span-4">Source type</div>
                  <div className="col-span-2 text-right">Assets</div>
                  <div className="col-span-2 text-right">Classified</div>
                  <div className="col-span-2 text-right">Classifications</div>
                  <div className="col-span-2 text-right">Sensitive</div>
                </div>
                {sourceAggregates.map((a, i) => (
                  <motion.div
                    key={a.sourceType}
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: Math.min(i * 0.02, 0.3) }}
                    className="grid grid-cols-12 items-center px-4 py-3 text-sm border-b border-border/30 hover:bg-muted/40 transition-colors"
                  >
                    <div className="col-span-4 font-mono text-xs">{a.sourceType}</div>
                    <div className="col-span-2 text-right tabular text-xs">
                      {a.assetCount.toLocaleString('en-IN')}
                    </div>
                    <div className="col-span-2 text-right tabular text-xs">
                      {a.classifiedAssetCount.toLocaleString('en-IN')}
                    </div>
                    <div className="col-span-2 text-right tabular text-xs">
                      {a.totalClassifications.toLocaleString('en-IN')}
                    </div>
                    <div className="col-span-2 text-right">
                      {a.sensitiveAssetCount > 0 ? (
                        <span className="inline-flex items-center gap-1.5 text-xs text-sev-high">
                          <span className="h-1.5 w-1.5 rounded-full bg-sev-high" />
                          <span className="tabular">{a.sensitiveAssetCount}</span>
                        </span>
                      ) : (
                        <span className="text-muted-foreground text-xs">-</span>
                      )}
                    </div>
                  </motion.div>
                ))}
              </GlassCard>

              <CrossSourceReconciliationCard />

              {/* Release 8, C5: stores ranked by personal-data density. */}
              <div className="mt-6">
                <StoreDensityCard onOpen={(id) => setDrawerAssetId(id)} />
              </div>

              {/* Asset catalog grid (doc 33): search / filter / sort over every
                  individual asset, virtualized so thousands stay smooth. Each row
                  opens the detail drawer (GET /v1/discovery/assets/:id). */}
              <GlassCard className="p-0 overflow-hidden mt-6">
                <div className="px-4 py-3 border-b border-border/40">
                  <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                    <div>
                      <CardTitle className="text-sm">Asset catalog</CardTitle>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Showing {catalogAssets.length.toLocaleString('en-IN')} of {totalAssets.toLocaleString('en-IN')} assets
                        {assets.length < totalAssets ? ` (the ${assets.length.toLocaleString('en-IN')} most recently scanned are loaded here)` : ''}. Click any row to inspect classifications, owner, and provenance.
                      </p>
                    </div>
                    <select
                      className="bg-card border border-border/40 rounded px-2 py-1 text-xs"
                      value={assetSort}
                      onChange={(e) => setAssetSort(e.target.value as 'risk' | 'name' | 'recent')}
                      aria-label="Sort assets"
                    >
                      <option value="risk">Sort: highest risk</option>
                      <option value="recent">Sort: recently scanned</option>
                      <option value="name">Sort: name (A-Z)</option>
                    </select>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="glass-lite flex items-center gap-2 rounded-md px-2.5 py-1 flex-1 min-w-[200px] max-w-sm">
                      <Search className="h-3.5 w-3.5 text-muted-foreground" />
                      <input
                        value={assetSearch}
                        onChange={(e) => setAssetSearch(e.target.value)}
                        placeholder="Search asset, source, owner, or classification..."
                        className="flex-1 bg-transparent outline-none text-xs placeholder:text-muted-foreground/70"
                      />
                    </div>
                    <select
                      className="bg-card border border-border/40 rounded px-2 py-1 text-xs"
                      value={assetSource}
                      onChange={(e) => setAssetSource(e.target.value)}
                      aria-label="Filter by source type"
                    >
                      <option value="all">All sources ({totalAssets})</option>
                      {sourceAggregates.map((s) => (
                        <option key={s.sourceType} value={s.sourceType}>{s.sourceType} ({s.assetCount})</option>
                      ))}
                    </select>
                    <button
                      onClick={() => setAssetSensitiveOnly((v) => !v)}
                      className={cn('rounded-full px-2.5 py-1 text-xs border transition-colors', assetSensitiveOnly ? 'border-sev-high/50 bg-sev-high/10 text-sev-high' : 'border-transparent hover:bg-muted/40 text-muted-foreground')}
                    >
                      Sensitive only
                    </button>
                    <button
                      onClick={() => setAssetUnownedOnly((v) => !v)}
                      className={cn('rounded-full px-2.5 py-1 text-xs border transition-colors', assetUnownedOnly ? 'border-warning/50 bg-warning/10 text-warning' : 'border-transparent hover:bg-muted/40 text-muted-foreground')}
                    >
                      Unowned only
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-12 px-4 py-2 text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border/40">
                  <div className="col-span-4">Asset</div>
                  <div className="col-span-2">Source</div>
                  <div className="col-span-3">Classifications</div>
                  <div className="col-span-2">Owner</div>
                  <div className="col-span-1 text-right">Scanned</div>
                </div>
                {catalogAssets.length === 0 ? (
                  <div className="p-8 text-center text-sm text-muted-foreground">
                    <Search className="h-7 w-7 mx-auto mb-2 opacity-40" />
                    <div className="font-medium text-foreground/90">No assets match your filters</div>
                    <button
                      onClick={() => { setAssetSearch(''); setAssetSource('all'); setAssetSensitiveOnly(false); setAssetUnownedOnly(false); }}
                      className="text-primary hover:underline mt-1 text-xs"
                    >
                      Clear filters
                    </button>
                  </div>
                ) : (
                  <VirtualList<DiscoveryAsset>
                    items={catalogAssets}
                    height={Math.min(560, Math.max(64, catalogAssets.length * 64))}
                    itemHeight={64}
                    getKey={(a) => a.id}
                    renderRow={(a) => <AssetCatalogRow asset={a} onOpen={() => setDrawerAssetId(a.id)} />}
                  />
                )}
              </GlassCard>
            </>
          ) : (
            <div className="glass-lite rounded-md p-8 text-center text-sm text-muted-foreground">
              <Database className="h-8 w-8 mx-auto mb-3 opacity-40" />
              <div className="font-medium text-foreground/90">No assets discovered yet</div>
              <div className="mt-1">
                Connect a data source from <strong>Connectors</strong> and run a discovery scan to populate this view.
              </div>
            </div>
          )}
        </>
      )}

      {/* Asset detail drawer - overlay sheet from the right. Closes on
          backdrop click or × button. */}
      {drawerAssetId && (
        <div
          className="fixed inset-0 z-50 bg-background/60 backdrop-blur-sm flex justify-end"
          onClick={closeDrawer}
          role="dialog"
          aria-modal="true"
          aria-label="Asset detail"
        >
          <div
            className="h-full w-full max-w-[640px] glass-strong border-l border-border/40 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 z-10 flex items-center gap-2 px-5 py-3 border-b border-border/40 glass-strong">
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Asset detail</div>
                <div className="text-sm font-mono truncate">
                  {drawerAsset?.displayName ?? drawerAssetId}
                </div>
              </div>
              <Button variant="glass" size="sm" onClick={closeDrawer} aria-label="Close drawer">
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="p-5 space-y-4 text-sm">
              {drawerLoading && (
                <div className="flex items-center gap-2 text-muted-foreground text-xs py-8 justify-center">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading asset detail…
                </div>
              )}
              {drawerError && !drawerLoading && (
                <div className="glass-lite border border-warning/40 rounded-md p-3 text-xs flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-warning shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div className="font-medium">Failed to load asset</div>
                    <div className="text-muted-foreground mt-0.5">{drawerError}</div>
                  </div>
                  <Button size="sm" variant="glass" onClick={() => { setDrawerAssetId(null); setDrawerAssetId(drawerAssetId); }}>
                    Retry
                  </Button>
                </div>
              )}
              {drawerAsset && !drawerLoading && (
                <>
                  <UploadedSchemaNote asset={drawerAsset} />
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Source type</div>
                    <code className="text-xs">{drawerAsset.sourceType}</code>
                  </div>
                  {Object.keys(drawerAsset.externalRefs ?? {}).length > 0 && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">External references</div>
                      <pre className="text-xs glass-lite rounded-md p-2 overflow-x-auto">
                        {JSON.stringify(drawerAsset.externalRefs, null, 2)}
                      </pre>
                    </div>
                  )}
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Sensitivity</div>
                    <Badge
                      variant={
                        drawerAsset.sensitivity === 'critical' || drawerAsset.sensitivity === 'high'
                          ? 'critical'
                          : drawerAsset.sensitivity === 'medium'
                            ? 'warning'
                            : 'glass'
                      }
                      className="text-[10px] capitalize"
                    >
                      {drawerAsset.sensitivity}
                    </Badge>
                    <span className="ml-2 text-[10px] text-muted-foreground">
                      confidence {(drawerAsset.confidenceScore * 100).toFixed(0)}%
                    </span>
                  </div>
                  {drawerAsset.rowCount != null && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Row count</div>
                      <code className="text-xs">{drawerAsset.rowCount.toLocaleString('en-IN')}</code>
                    </div>
                  )}
                  {/* Item-1: data-owner (custodian) assignment - orphan assets
                      become accountable here (DPDPA). PATCH /assets/:id/owner. */}
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Data owner</div>
                    {drawerAsset.ownerEmail ? (
                      <code className="text-xs">{drawerAsset.ownerEmail}</code>
                    ) : (
                      <span className="text-xs text-warning inline-flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" /> Unassigned (orphan)
                      </span>
                    )}
                    <div className="mt-2 flex items-center gap-2">
                      <input
                        type="email"
                        value={ownerInput}
                        onChange={(e) => setOwnerInput(e.target.value)}
                        placeholder="owner@company.com"
                        className="flex-1 text-xs bg-background/50 border border-border/50 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-primary/50"
                      />
                      <Button
                        size="sm"
                        variant="glass"
                        disabled={assigning || !ownerInput.trim() || ownerInput.trim() === (drawerAsset.ownerEmail ?? '')}
                        onClick={handleAssignOwner}
                        data-action="disc.dspm_inventory.a01"
                      >
                        {assigning ? 'Assigning…' : 'Assign'}
                      </Button>
                    </div>
                    {assignError && <div className="text-[10px] text-sev-high mt-1">{assignError}</div>}
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Classifications</div>
                    {(drawerAsset.classifications ?? []).length === 0 ? (
                      <div className="text-xs text-muted-foreground">No classifications applied yet.</div>
                    ) : (
                      <div className="flex flex-wrap gap-1.5">
                        {drawerAsset.classifications!.map((c, i) => (
                          <Badge key={`${c.label}-${i}`} variant="glass" className="text-[10px]">
                            {c.label}{c.confidence != null && (
                              <span className="ml-1 text-muted-foreground">· {(Number(c.confidence) * 100).toFixed(0)}%</span>
                            )}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                  {/* Annexure H Part I 111: a protected copy of this table in
                      the customer's own database. PostgreSQL only for now. */}
                  {drawerAsset.sourceType === 'postgres' && <ProtectedCopyPanel assetId={drawerAsset.id} />}
                  {drawerAsset.lastScannedAt && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Last scanned</div>
                      <code className="text-xs">{new Date(drawerAsset.lastScannedAt).toLocaleString()}</code>
                    </div>
                  )}
                  {/* Annexure H Part I 114: how easily a person could be picked out
                      of this table once shared. PostgreSQL only for now. */}
                  {drawerAsset.sourceType === 'postgres' && <ReidentificationRiskPanel assetId={drawerAsset.id} />}
                  {/* Raw record for debugging - collapsible. */}
                  <details className="text-xs">
                    <summary className="cursor-pointer text-muted-foreground">Raw record</summary>
                    <pre className="mt-2 p-3 glass-lite rounded-md overflow-x-auto text-[10px]">
                      {JSON.stringify(drawerAsset, null, 2)}
                    </pre>
                  </details>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </PageContainer>
  );
}

const SENS_SHORT: Record<string, string> = { critical: 'CRIT', high: 'HIGH', medium: 'MED', low: 'LOW', unknown: '?' };

/** One asset row in the virtualized catalog grid (doc 33). Sized to 64px. */
export function AssetCatalogRow({ asset, onOpen }: { asset: DiscoveryAsset; onOpen: () => void }) {
  const a = asset;
  const sens = a.sensitivity || 'unknown';
  const cls = a.classifications ?? [];
  const uploaded = isUploadedSchemaAsset(a);
  return (
    <button
      type="button"
      onClick={onOpen}
      className="w-full h-16 grid grid-cols-12 items-center gap-2 px-4 text-sm border-b border-border/30 hover:bg-muted/40 transition-colors text-left"
    >
      <div className="col-span-4 min-w-0 flex items-center gap-2">
        <span className={cn('inline-flex items-center justify-center rounded px-1.5 py-0.5 text-[9px] font-semibold w-11 shrink-0', SENS_PILL_CLS[sens] ?? SENS_PILL_CLS.unknown)}>
          {SENS_SHORT[sens] ?? '?'}
        </span>
        <span className="font-mono text-xs truncate">{a.displayName || `${a.id.slice(0, 20)}…`}</span>
        {uploaded && (
          <Badge
            variant="info"
            className="text-[8px] shrink-0"
            title="A person uploaded this schema. Inferred from column names; no scanner read the data. Kept apart from any scanned asset with the same name."
          >
            uploaded schema
          </Badge>
        )}
      </div>
      <div className="col-span-2 text-xs text-muted-foreground truncate">{a.sourceType}</div>
      <div className="col-span-3 flex flex-wrap gap-1 min-w-0 overflow-hidden">
        {cls.slice(0, 3).map((c, idx) => (
          <Badge key={`${c.label}-${idx}`} variant="glass" className="text-[9px]">{c.label}</Badge>
        ))}
        {cls.length > 3 && <span className="text-[9px] text-muted-foreground">+{cls.length - 3}</span>}
        {cls.length === 0 && <span className="text-[10px] text-muted-foreground italic">unclassified</span>}
      </div>
      <div className="col-span-2 text-xs min-w-0">
        {a.ownerEmail ? (
          <span className="flex items-center gap-1 text-foreground/80 truncate"><User className="h-3 w-3 opacity-60 shrink-0" /><span className="truncate">{a.ownerEmail}</span></span>
        ) : (
          <span className="inline-flex items-center gap-1 text-warning text-[10px]"><AlertTriangle className="h-3 w-3" /> orphan</span>
        )}
      </div>
      <div className="col-span-1 text-right text-[10px] text-muted-foreground">
        {a.lastScannedAt ? new Date(a.lastScannedAt).toLocaleDateString() : uploaded ? 'not scanned' : '-'}
      </div>
    </button>
  );
}
